import { supabase } from '@/integrations/supabase/client';

export interface Holiday {
  id: string;
  country_code: string;
  year: number;
  date: string;
  name: string;
  type: 'public' | 'observance';
  is_non_working_day: boolean;
  source: string;
  description?: string;
}

// Country code patterns for extracting country from address
const COUNTRY_PATTERNS: Record<string, string> = {
  // Major Non-EU Countries
  'United States': 'US',
  'USA': 'US',
  'U.S.A': 'US',
  'United Kingdom': 'GB',
  'UK': 'GB',
  'Great Britain': 'GB',
  'England': 'GB',
  'Scotland': 'GB',
  'Wales': 'GB',
  'Canada': 'CA',
  'Australia': 'AU',
  'New Zealand': 'NZ',
  'Japan': 'JP',
  'China': 'CN',
  'India': 'IN',
  
  // EU Countries
  'Germany': 'DE',
  'Deutschland': 'DE',
  'France': 'FR',
  'République française': 'FR',
  'Spain': 'ES',
  'España': 'ES',
  'Italy': 'IT',
  'Italia': 'IT',
  'Netherlands': 'NL',
  'Nederland': 'NL',
  'Belgium': 'BE',
  'België': 'BE',
  'Belgique': 'BE',
  'Austria': 'AT',
  'Österreich': 'AT',
  'Switzerland': 'CH',
  'Schweiz': 'CH',
  'Suisse': 'CH',
  'Sweden': 'SE',
  'Sverige': 'SE',
  'Norway': 'NO',
  'Norge': 'NO',
  'Denmark': 'DK',
  'Danmark': 'DK',
  'Finland': 'FI',
  'Suomi': 'FI',
  'Poland': 'PL',
  'Polska': 'PL',
  'Czech Republic': 'CZ',
  'Česká republika': 'CZ',
  'Slovakia': 'SK',
  'Slovensko': 'SK',
  'Hungary': 'HU',
  'Magyarország': 'HU',
  'Romania': 'RO',
  'România': 'RO',
  'Bulgaria': 'BG',
  'България': 'BG',
  'Croatia': 'HR',
  'Hrvatska': 'HR',
  'Slovenia': 'SI',
  'Slovenija': 'SI',
  'Lithuania': 'LT',
  'Lietuva': 'LT',
  'Latvia': 'LV',
  'Latvija': 'LV',
  'Estonia': 'EE',
  'Eesti': 'EE',
  'Ireland': 'IE',
  'Éire': 'IE',
  'Portugal': 'PT',
  'República Portuguesa': 'PT',
  'Greece': 'GR',
  'Ελλάδα': 'GR',
  'Cyprus': 'CY',
  'Κύπρος': 'CY',
  'Malta': 'MT',
  'Luxembourg': 'LU',
};

// Supported countries for holiday detection
const SUPPORTED_COUNTRIES = new Set([
  'US', 'GB', 'CA', 'AU', 'NZ', 'JP', 'CN', 'IN',
  'DE', 'FR', 'ES', 'IT', 'NL', 'BE', 'AT', 'CH',
  'SE', 'NO', 'DK', 'FI', 'PL', 'CZ', 'SK', 'HU',
  'RO', 'BG', 'HR', 'SI', 'LT', 'LV', 'EE', 'IE',
  'PT', 'GR', 'CY', 'MT', 'LU'
]);

/**
 * Extract country code from address string
 */
export function extractCountryFromAddress(address: string): string | null {
  if (!address) return null;
  
  // Check for country patterns in the address
  for (const [pattern, code] of Object.entries(COUNTRY_PATTERNS)) {
    if (address.includes(pattern)) {
      return code;
    }
  }
  
  // Try to extract from the last part of the address (usually the country)
  const parts = address.split(',').map(p => p.trim());
  const lastPart = parts[parts.length - 1];
  
  for (const [pattern, code] of Object.entries(COUNTRY_PATTERNS)) {
    if (lastPart.toLowerCase() === pattern.toLowerCase()) {
      return code;
    }
  }
  
  return null;
}

/**
 * Check if a country is supported for holiday detection
 */
export function isCountrySupported(countryCode: string): boolean {
  return SUPPORTED_COUNTRIES.has(countryCode);
}

/**
 * Get holidays for a specific country and year from Supabase
 */
export async function getHolidaysForCountry(countryCode: string, year: number): Promise<Holiday[]> {
  if (!isCountrySupported(countryCode)) {
    console.warn(`Country ${countryCode} is not supported for holiday detection`);
    return [];
  }

  try {
    const { data, error } = await supabase
      .from('holidays')
      .select('*')
      .eq('country_code', countryCode)
      .eq('year', year)
      .order('date');

    if (error) {
      console.error(`Error fetching holidays for ${countryCode} ${year}:`, error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error(`Error fetching holidays for ${countryCode} ${year}:`, error);
    return [];
  }
}

/**
 * Get holidays for a date range from Supabase
 */
export async function getHolidaysInRange(countryCode: string, startDate: Date, endDate: Date): Promise<Holiday[]> {
  if (!isCountrySupported(countryCode)) {
    console.warn(`Country ${countryCode} is not supported for holiday detection`);
    return [];
  }

  try {
    const startDateStr = startDate.toISOString().split('T')[0];
    const endDateStr = endDate.toISOString().split('T')[0];

    const { data, error } = await supabase
      .from('holidays')
      .select('*')
      .eq('country_code', countryCode)
      .gte('date', startDateStr)
      .lte('date', endDateStr)
      .order('date');

    if (error) {
      console.error(`Error fetching holidays for ${countryCode} in range ${startDateStr} to ${endDateStr}:`, error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error(`Error fetching holidays for ${countryCode} in range:`, error);
    return [];
  }
}

/**
 * Check if a specific date is a holiday
 */
export async function isHoliday(date: Date, countryCode: string): Promise<Holiday | null> {
  const dateStr = date.toISOString().split('T')[0];
  const year = date.getFullYear();
  
  try {
    const { data, error } = await supabase
      .from('holidays')
      .select('*')
      .eq('country_code', countryCode)
      .eq('date', dateStr)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
      console.error(`Error checking if ${dateStr} is a holiday in ${countryCode}:`, error);
      return null;
    }

    return data || null;
  } catch (error) {
    console.error(`Error checking if ${dateStr} is a holiday in ${countryCode}:`, error);
    return null;
  }
}

/**
 * Check if a date is a non-working day (holiday or weekend)
 */
export async function isNonWorkingDay(date: Date, countryCode: string, includeWeekends: boolean = true): Promise<boolean> {
  // Check if it's a weekend
  if (includeWeekends) {
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) { // Sunday or Saturday
      return true;
    }
  }

  // Check if it's a holiday that's marked as non-working
  const holiday = await isHoliday(date, countryCode);
  return holiday?.is_non_working_day || false;
}

/**
 * Get holiday information for a specific date and upcoming holidays
 */
export async function getHolidayInfo(countryCode: string, date: Date = new Date()) {
  if (!isCountrySupported(countryCode)) {
    console.warn(`Country ${countryCode} is not supported for holiday detection`);
    return {
      todayHoliday: null,
      upcomingHoliday: null
    };
  }

  const today = date.toISOString().split('T')[0];
  const nextYear = new Date(date.getFullYear() + 1, date.getMonth(), date.getDate()).toISOString().split('T')[0];

  try {
    const { data: holidays, error } = await supabase
      .from('holidays')
      .select('date, name')
      .eq('country_code', countryCode.toUpperCase())
      .gte('date', today)
      .lte('date', nextYear)
      .order('date', { ascending: true });

    if (error) {
      console.error(`Error fetching holidays for ${countryCode}:`, error);
      return {
        todayHoliday: null,
        upcomingHoliday: null
      };
    }

    // Find today's holiday
    const todayHoliday = holidays?.find(h => h.date === today) || null;

    // Find next upcoming holiday (not today)
    const upcomingHoliday = holidays?.find(h => h.date > today);
    
    if (upcomingHoliday) {
      const daysUntil = Math.ceil((new Date(upcomingHoliday.date).getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
      return {
        todayHoliday,
        upcomingHoliday: {
          ...upcomingHoliday,
          daysUntil
        }
      };
    }

    return {
      todayHoliday,
      upcomingHoliday: null
    };
  } catch (error) {
    console.error(`Error fetching holidays for ${countryCode}:`, error);
    return {
      todayHoliday: null,
      upcomingHoliday: null
    };
  }
}

/**
 * Get holidays for multiple years (useful for calendar views)
 */
export async function getHolidaysForYears(countryCode: string, years: number[]): Promise<Record<number, Holiday[]>> {
  if (!isCountrySupported(countryCode)) {
    console.warn(`Country ${countryCode} is not supported for holiday detection`);
    return {};
  }

  try {
    const { data, error } = await supabase
      .from('holidays')
      .select('*')
      .eq('country_code', countryCode)
      .in('year', years)
      .order('date');

    if (error) {
      console.error(`Error fetching holidays for ${countryCode} years ${years.join(', ')}:`, error);
      return {};
    }

    // Group holidays by year
    const holidaysByYear: Record<number, Holiday[]> = {};
    years.forEach(year => {
      holidaysByYear[year] = [];
    });

    (data || []).forEach(holiday => {
      if (holidaysByYear[holiday.year]) {
        holidaysByYear[holiday.year].push(holiday);
      }
    });

    return holidaysByYear;
  } catch (error) {
    console.error(`Error fetching holidays for ${countryCode} years ${years.join(', ')}:`, error);
    return {};
  }
}

/**
 * Populate holidays for a country and year using the Edge Function
 */
export async function populateHolidays(countryCode: string[], years: number[]): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke('populate-holidays', {
      body: {
        country_codes: countryCode,
        years: years
      }
    });

    if (error) {
      console.error('Error populating holidays:', error);
      throw error;
    }

    console.log('Holidays populated successfully:', data);
  } catch (error) {
    console.error('Error calling populate-holidays function:', error);
    throw error;
  }
}

/**
 * Get list of supported countries
 */
export function getSupportedCountries(): string[] {
  return Array.from(SUPPORTED_COUNTRIES);
}

/**
 * Get country name from country code
 */
export function getCountryName(countryCode: string): string {
  const countryNames: Record<string, string> = {
    US: 'United States',
    GB: 'United Kingdom',
    CA: 'Canada',
    AU: 'Australia',
    NZ: 'New Zealand',
    JP: 'Japan',
    CN: 'China',
    IN: 'India',
    DE: 'Germany',
    FR: 'France',
    ES: 'Spain',
    IT: 'Italy',
    NL: 'Netherlands',
    BE: 'Belgium',
    AT: 'Austria',
    CH: 'Switzerland',
    SE: 'Sweden',
    NO: 'Norway',
    DK: 'Denmark',
    FI: 'Finland',
    PL: 'Poland',
    CZ: 'Czech Republic',
    SK: 'Slovakia',
    HU: 'Hungary',
    RO: 'Romania',
    BG: 'Bulgaria',
    HR: 'Croatia',
    SI: 'Slovenia',
    LT: 'Lithuania',
    LV: 'Latvia',
    EE: 'Estonia',
    IE: 'Ireland',
    PT: 'Portugal',
    GR: 'Greece',
    CY: 'Cyprus',
    MT: 'Malta',
    LU: 'Luxembourg',
  };

  return countryNames[countryCode] || countryCode;
}